import React, { useState } from "react";
import { showAlert } from "../../alertService";
import { db } from "../../firebase";
import { BITACORA_ACTIONS, logBitacora } from "../../utils/bitacora";
import { Alert } from "../alert";
import { Badge } from "../badge";
import { Button } from "../button";
import { Heading } from "../heading";
import { ACCENT, BTN_PRIMARIO, newId } from "./shared";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import {
  CheckIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon
} from "@heroicons/react/20/solid";

// ---------- Contactos tab ----------
const ContactosTab = ({ proveedor, venueId, userInfo, user, soloLectura = false }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const contactos = proveedor.contactos || [];

  const persist = async (newContactos, action, contactInfo) => {
    const docRef = doc(db, "venues", venueId, "proveedores", proveedor.id);
    try {
      await updateDoc(docRef, {
        contactos: newContactos,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      logBitacora(
        venueId,
        {
          action,
          entity: "proveedor_contacto",
          entityId: contactInfo?.id || proveedor.id,
          entityName: contactInfo?.nombre || proveedor.nombre || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre || "",
            contactoNombre: contactInfo?.nombre || "",
          },
        },
        userInfo,
        user
      );
    } catch (e) {
      console.error(e);
      showAlert("Error al guardar: " + e.message, "error");
    }
  };

  const startEdit = (contacto) => {
    setEditingId(contacto.id);
    setDraft({ ...contacto });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const saveEdit = async () => {
    if (!draft || !draft.nombre?.trim()) return;
    const cleaned = {
      ...draft,
      nombre: draft.nombre.trim(),
      rol: draft.rol?.trim() || null,
      email: draft.email?.trim() || null,
      telefono: draft.telefono?.trim() || null,
      notas: draft.notas?.trim() || null,
    };
    let nextContactos = contactos.map((c) =>
      c.id === draft.id ? cleaned : c
    );
    // Ensure only one esPrincipal
    if (cleaned.esPrincipal) {
      nextContactos = nextContactos.map((c) =>
        c.id === cleaned.id ? c : { ...c, esPrincipal: false }
      );
    }
    await persist(nextContactos, BITACORA_ACTIONS.PROVEEDOR_CONTACTO_EDIT, cleaned);
    cancelEdit();
  };

  const handleAdd = async (data) => {
    if (!data.nombre?.trim()) return;
    const contacto = {
      id: newId(),
      nombre: data.nombre.trim(),
      rol: data.rol?.trim() || null,
      email: data.email?.trim() || null,
      telefono: data.telefono?.trim() || null,
      esPrincipal: contactos.length === 0,
      notas: null,
    };
    await persist(
      [...contactos, contacto],
      BITACORA_ACTIONS.PROVEEDOR_CONTACTO_ADD,
      contacto
    );
    setShowAdd(false);
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    const next = contactos.filter((c) => c.id !== deleteCandidate.id);
    // If we removed the principal and there's another, promote first
    if (deleteCandidate.esPrincipal && next.length > 0) {
      next[0] = { ...next[0], esPrincipal: true };
    }
    await persist(
      next,
      BITACORA_ACTIONS.PROVEEDOR_CONTACTO_DELETE,
      deleteCandidate
    );
    setDeleteCandidate(null);
  };

  const handleTogglePrincipal = async (contacto) => {
    if (contacto.esPrincipal) return; // already principal — keep it
    const next = contactos.map((c) => ({
      ...c,
      esPrincipal: c.id === contacto.id,
    }));
    await persist(
      next,
      BITACORA_ACTIONS.PROVEEDOR_CONTACTO_EDIT,
      contacto
    );
  };

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <Heading level={3}>Personas de contacto</Heading>
        {!soloLectura && (
        <button
          onClick={() => setShowAdd(true)}
          className={BTN_PRIMARIO}
          style={{ backgroundColor: ACCENT }}
        >
          <PlusIcon className="h-4 w-4" />
          Agregar
        </button>
        )}
      </div>

      {contactos.length === 0 && (
        <div className="text-sm text-zinc-400 italic py-6 text-center">
          Aún no hay contactos.
        </div>
      )}

      <div className="space-y-3">
        {contactos.map((c) => {
          const isEditing = editingId === c.id;
          return (
            <div
              key={c.id}
              className="rounded-lg border border-zinc-200 p-4 bg-zinc-50/50"
            >
              {isEditing ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      placeholder="Nombre"
                      value={draft.nombre || ""}
                      onChange={(e) =>
                        setDraft({ ...draft, nombre: e.target.value })
                      }
                      className={inputClass}
                    />
                    <input
                      placeholder="Rol (ej. Gerente)"
                      value={draft.rol || ""}
                      onChange={(e) =>
                        setDraft({ ...draft, rol: e.target.value })
                      }
                      className={inputClass}
                    />
                    <input
                      placeholder="Email"
                      type="email"
                      value={draft.email || ""}
                      onChange={(e) =>
                        setDraft({ ...draft, email: e.target.value })
                      }
                      className={inputClass}
                    />
                    <input
                      placeholder="Teléfono"
                      type="tel"
                      value={draft.telefono || ""}
                      onChange={(e) =>
                        setDraft({ ...draft, telefono: e.target.value })
                      }
                      className={inputClass}
                    />
                  </div>
                  <textarea
                    placeholder="Notas"
                    value={draft.notas || ""}
                    onChange={(e) =>
                      setDraft({ ...draft, notas: e.target.value })
                    }
                    rows={2}
                    className={inputClass}
                  />
                  <label className="flex items-center gap-2 text-sm text-zinc-600">
                    <input
                      type="checkbox"
                      checked={!!draft.esPrincipal}
                      onChange={(e) =>
                        setDraft({ ...draft, esPrincipal: e.target.checked })
                      }
                    />
                    Contacto principal
                  </label>
                  <div className="flex justify-end gap-2">
                    <Button outline onClick={cancelEdit}>
                      <XMarkIcon className="h-4 w-4 mr-1" /> Cancelar
                    </Button>
                    <Button color="dark" onClick={saveEdit}>
                      <CheckIcon className="h-4 w-4 mr-1" /> Guardar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-zinc-900">
                        {c.nombre}
                      </span>
                      {c.rol && (
                        <span className="text-xs text-zinc-500">— {c.rol}</span>
                      )}
                      {c.esPrincipal && <Badge color="emerald">Principal</Badge>}
                    </div>
                    <div className="mt-1 text-sm text-zinc-600 space-x-3">
                      {c.email && (
                        <a
                          href={`mailto:${c.email}`}
                          className="hover:underline"
                        >
                          {c.email}
                        </a>
                      )}
                      {c.telefono && (
                        <a
                          href={`tel:${c.telefono}`}
                          className="hover:underline"
                        >
                          {c.telefono}
                        </a>
                      )}
                    </div>
                    {c.notas && (
                      <div className="mt-2 text-sm text-zinc-500 whitespace-pre-wrap">
                        {c.notas}
                      </div>
                    )}
                  </div>
                  {!soloLectura && (
                  <div className="flex flex-col items-end gap-1">
                    {!c.esPrincipal && (
                      <button
                        onClick={() => handleTogglePrincipal(c)}
                        className="text-xs text-zinc-500 hover:text-[#1b4d2e]"
                      >
                        Marcar principal
                      </button>
                    )}
                    <div className="flex gap-1">
                      <button
                        onClick={() => startEdit(c)}
                        className="text-zinc-400 hover:text-zinc-600 p-1"
                        title="Editar"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteCandidate(c)}
                        className="text-zinc-400 hover:text-red-500 p-1"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showAdd && (
        <AddContactoForm
          onCancel={() => setShowAdd(false)}
          onSubmit={handleAdd}
        />
      )}

      <Alert open={!!deleteCandidate} onClose={() => setDeleteCandidate(null)}>
        <div className="font-semibold mb-2">
          ¿Eliminar contacto "{deleteCandidate?.nombre}"?
        </div>
        <div className="mb-4 text-sm text-zinc-600">
          Esta acción no se puede deshacer.
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={() => setDeleteCandidate(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleDelete}>
            Eliminar
          </Button>
        </div>
      </Alert>
    </div>
  );
};

const AddContactoForm = ({ onCancel, onSubmit }) => {
  const [data, setData] = useState({
    nombre: "",
    rol: "",
    email: "",
    telefono: "",
  });
  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  return (
    <div className="mt-4 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800/50 p-4">
      <div className="font-medium text-sm mb-3">Nuevo contacto</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input
          autoFocus
          placeholder="Nombre"
          value={data.nombre}
          onChange={(e) => setData({ ...data, nombre: e.target.value })}
          className={inputClass}
        />
        <input
          placeholder="Rol (ej. Gerente)"
          value={data.rol}
          onChange={(e) => setData({ ...data, rol: e.target.value })}
          className={inputClass}
        />
        <input
          type="email"
          placeholder="Email"
          value={data.email}
          onChange={(e) => setData({ ...data, email: e.target.value })}
          className={inputClass}
        />
        <input
          type="tel"
          placeholder="Teléfono"
          value={data.telefono}
          onChange={(e) => setData({ ...data, telefono: e.target.value })}
          className={inputClass}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button outline onClick={onCancel}>
          Cancelar
        </Button>
        <button
          onClick={() => onSubmit(data)}
          disabled={!data.nombre.trim()}
          className={BTN_PRIMARIO}
          style={{ backgroundColor: ACCENT }}
        >
          Agregar
        </button>
      </div>
    </div>
  );
};

export default ContactosTab;
